import { useRef, useState } from 'react'
import { useEffect } from 'react'

import { HamburgerIcon, LockIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Flex,
  Heading,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  Tooltip,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  ArrowsRightLeftIcon,
  FolderOpenIcon,
  LockClosedIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/solid'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { loadCaseworkInstance } from '../../redux/features/caseworkSlice'
import { ActivityCard, CaseworkDetailTable } from '../casework'
import {
  ArchiveDialog,
  CloseDialog,
  DeleteDialog,
  ReopenDialog,
  TransferDialog,
  UnarchiveDialog,
} from '../casework/casework/actionDialogs'
import PriorCaseworkAlert from '../casework/casework/PriorCaseworkAlert'
import {
  CaseworkCMList,
  ConditionalRender,
  Loading,
  NotFound,
  Person,
  Section,
} from '../ui'
import { formatCaseNum } from '../utils'

const CaseworkDetail = () => {
  const { case_num } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const loading = useSelector(state => state.casework.loading)
  const caseworkInstance = useSelector(
    state => state.casework.casework_instance
  )

  const [actionStatus, setActionStatus] = useState('')
  const headingRef = useRef(null)

  const caseworkTextColor = useColorModeValue('#23417D', 'white.900')

  useEffect(() => {
    dispatch(
      loadCaseworkInstance({
        case_num,
        archive: location.pathname.includes('/archive'),
      })
    )
  }, [dispatch, case_num])

  const {
    isOpen: confirmIsOpen,
    onOpen: confirmOnOpen,
    onClose: confirmOnClose,
  } = useDisclosure()

  if (loading) return <Loading />

  if (!caseworkInstance) return <NotFound />

  document.title = `Casework #${formatCaseNum(
    caseworkInstance
  )} | Council Connect`

  const {
    intake_staff,
    assigned_staff,
    status,
    topics,
    visible_to_district_only,
    is_active,
    is_prior,
    actions: {
      closeable,
      editable,
      reopenable,
      archivable,
      transferable,
      deletable,
    },
  } = caseworkInstance

  const additionalActions = [
    archivable && 'archive',
    deletable && 'delete',
    transferable && 'transfer',
  ].filter(Boolean)

  const dialogProps = {
    isOpen: confirmIsOpen,
    onClose: confirmOnClose,
    leastDestructiveRef: headingRef,
    case_num,
  }

  return (
    <Container maxW='8xl' p={10}>
      <Flex align='center' mb={4}>
        <Box>
          <Heading
            size='2xl'
            fontWeight={900}
            color={caseworkTextColor}
            ref={headingRef}
          >
            #{formatCaseNum(caseworkInstance)}
          </Heading>
        </Box>
        <Spacer />
        <ButtonGroup>
          <ConditionalRender condition={visible_to_district_only}>
            <Tooltip label='Casework is visible to district only'>
              <IconButton icon={<LockIcon boxSize={6} />} variant='unstyled' />
            </Tooltip>
          </ConditionalRender>

          <ConditionalRender
            condition={status === 'Archived'}
            permissions={['casework.unarchive_casework']}
          >
            <Button
              leftIcon={<Icon as={ArchiveBoxXMarkIcon} />}
              textTransform='uppercase'
              variant='outline'
              onClick={() => {
                setActionStatus('Unarchive')
                confirmOnOpen()
              }}
            >
              Unarchive
            </Button>
          </ConditionalRender>
          <ConditionalRender condition={editable}>
            <Tooltip
              label={
                topics?.length > 0
                  ? ''
                  : 'Topic must be assigned to close this case!'
              }
            >
              <Button
                leftIcon={<Icon as={LockClosedIcon} />}
                textTransform='uppercase'
                variant='outline'
                onClick={() => {
                  setActionStatus('Closed')
                  confirmOnOpen()
                }}
                isDisabled={!closeable}
              >
                Close
              </Button>
            </Tooltip>
            <Button
              leftIcon={<Icon as={PencilSquareIcon} />}
              textTransform='uppercase'
              variant='outline'
              onClick={() => navigate(`/casework/${case_num}/edit`)}
            >
              Edit
            </Button>
          </ConditionalRender>

          <ConditionalRender condition={reopenable}>
            <Button
              leftIcon={<Icon as={FolderOpenIcon} />}
              textTransform='uppercase'
              variant='outline'
              onClick={() => {
                setActionStatus('Reopen')
                confirmOnOpen()
              }}
            >
              Reopen
            </Button>
          </ConditionalRender>

          {additionalActions.length > 0 && (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label='Additional Casework Actions'
                icon={<HamburgerIcon />}
                variant='outline'
              />
              <Box>
                <MenuList>
                  {additionalActions.includes('transfer') && (
                    <MenuItem
                      icon={<Icon as={ArrowsRightLeftIcon} />}
                      onClick={() => {
                        setActionStatus('Transfer')
                        confirmOnOpen()
                      }}
                    >
                      TRANSFER
                    </MenuItem>
                  )}

                  {additionalActions.includes('archive') && (
                    <MenuItem
                      icon={<Icon as={ArchiveBoxIcon} />}
                      onClick={() => {
                        setActionStatus('Archive')
                        confirmOnOpen()
                      }}
                    >
                      ARCHIVE
                    </MenuItem>
                  )}

                  {additionalActions.includes('delete') && (
                    <MenuItem
                      icon={<Icon as={TrashIcon} />}
                      onClick={() => {
                        setActionStatus('Delete')
                        confirmOnOpen()
                      }}
                      color='red'
                    >
                      DELETE
                    </MenuItem>
                  )}
                </MenuList>
              </Box>
            </Menu>
          )}
        </ButtonGroup>
      </Flex>
      <ConditionalRender condition={is_active && is_prior}>
        <Flex py={2}>
          <PriorCaseworkAlert casework={caseworkInstance} origin='detail' />
        </Flex>
      </ConditionalRender>
      <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
        <CaseworkDetailTable {...caseworkInstance} detailView />

        <Spacer />
        <Flex direction='column' gap={5}>
          <Section heading='Assigned Staff'>
            {assigned_staff?.length > 0 ? (
              assigned_staff.map((staff, index) => (
                <Person
                  key={index}
                  name={staff.full_name}
                  deactivated={!staff.is_active}
                  link={`/directory/staff/${staff.id}`}
                />
              ))
            ) : (
              <Text as='em'>No staff has been assigned to this casework.</Text>
            )}
          </Section>
          {intake_staff && (
            <Section heading='Intake By'>
              <Person
                name={intake_staff.full_name}
                link={`/directory/staff/${intake_staff.id}`}
              />
            </Section>
          )}
          <Section heading='Council Member'>
            <CaseworkCMList casework={caseworkInstance} />
          </Section>
          <ActivityCard />
        </Flex>
      </Flex>
      {actionStatus === 'Delete' && <DeleteDialog {...dialogProps} />}
      {actionStatus === 'Transfer' && <TransferDialog {...dialogProps} />}
      {actionStatus === 'Archive' && <ArchiveDialog {...dialogProps} />}
      {actionStatus === 'Unarchive' && <UnarchiveDialog {...dialogProps} />}
      {actionStatus === 'Closed' && <CloseDialog {...dialogProps} />}
      {actionStatus === 'Reopen' && <ReopenDialog {...dialogProps} />}
    </Container>
  )
}

export default CaseworkDetail
